import React, { useState, useMemo } from 'react';
import { ArrowRight, MapPin, Sparkles, Clock, Calendar, Users, Wallet, Building2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { RESORT_FINANCIALS, fmtEUR } from '../lib/financial';
import { useQualification } from '../contexts/QualificationContext';

/**
 * ★ 2026-08-12 (Fase 44) — Refactor: tabella COMPLETAMENTE DINAMICA.
 *
 * Aggiorna automaticamente ogni volta che un progetto viene aggiunto/rimosso
 * da RESORT_FINANCIALS (le card sono sincronizzate al backend).
 *
 * - Desktop: N-column table (auto layout, scroll-x se serve)
 * - Mobile: tab switcher per vedere 1 progetto per volta
 *
 * CONSOB COMPLIANCE:
 * Non vengono mostrati target di IRR/Cash Yield in tabella. Le eventuali
 * proiezioni sono indicative e disponibili nei project financing ufficiali
 * della singola SPV, condivisi solo in fase di qualifica.
 */
const ROWS = [
  { key: 'country', label: 'Paese', icon: MapPin },
  { key: 'opening', label: 'Apertura', icon: Calendar },
  { key: 'minEntry', label: 'Ticket minimo', icon: Wallet, highlight: true },
  { key: 'participation', label: 'Tipologia di partecipazione', icon: Sparkles },
  { key: 'horizon', label: 'Orizzonte indicativo', icon: Clock },
  { key: 'units', label: 'Appartamenti · Disponibili', icon: Building2 },
  { key: 'unitsPerBlock', label: 'Unità per blocco', icon: Users },
  { key: 'capex', label: 'CAPEX progetto' },
  { key: 'realEstate', label: 'Real Estate disponibile' },
  { key: 'fitFor', label: 'Indicato per' },
];

// Fit-for statement default per resort (fallback se non definito nel financial entry)
// ★ 2026-08-28 — Rimossi lombok/spille/chumphon (progetti eliminati dall'ecosistema IT).
const DEFAULT_FIT_FOR = {
  'la-mar-residence': 'Chi cerca Real Estate fronte mare in Albania con consegna 2027',
  narea: 'Chi cerca un landmark residenziale iconico in Albania con 161 unità e consegna 2029',
  'palm-paradise': 'Chi cerca un quartiere residenziale tropicale in Albania con 198 unità e consegna Q2 2027',
  'vista-mare': 'Chi cerca un appartamento vista Adriatico con ticket accessibile e consegna Aprile 2027',
  'green-paradise-lombok': 'Chi cerca una villa privata in un resort eco-luxury in Indonesia con gestione hospitality',
};

const buildRow = (rKey) => {
  const r = RESORT_FINANCIALS[rKey];
  if (!r) return null;
  const units = r.units || { total: r.blocks?.total, available: r.blocks?.available };
  const shortName = (r.name || rKey)
    .replace(/^Green Paradise\s*/i, '')
    .replace(/^HCF Property\s*/i, '');
  return {
    country: `${r.flag || ''} ${r.country || ''}`.trim(),
    opening: r.grandOpening,
    minEntry: r.realEstate?.available && r.realEstate?.startingFrom ? fmtEUR(r.realEstate.startingFrom) : '—',
    participation: 'Real Estate (proprietà)',
    horizon: r.realEstate?.available ? `Consegna ${r.grandOpening}` : `Consegna ${r.grandOpening || '—'}`,
    units: `${units.total} · ${units.available > 0 ? `${units.available} disp.` : 'SOLD OUT'}`,
    unitsSoldOut: units.available === 0,
    unitsEquityOnly: false,
    unitsPerBlock: r.blocks?.perBlock || '—',
    capex: r.capex || '—',
    realEstate: r.realEstate?.available
      ? `Da ${fmtEUR(r.realEstate.startingFrom)}${r.promo?.active ? ` · -${r.promo.discountPct}% pre-launch` : ''}`
      : r.realEstate?.soldOut
        ? 'SOLD OUT · Nuove disponibilità in comunicazione'
        : 'In pubblicazione',
    realEstateAvailable: !!r.realEstate?.available,
    fitFor: r.fitFor || DEFAULT_FIT_FOR[rKey] || `Chi cerca un progetto in ${r.country || 'nuova destinazione'}`,
    name: r.name || rKey,
    shortName,
  };
};

export default function ResortCompareTable({ activeSlugs }) {
  const { open: openQualifica } = useQualification();

  // ★ 2026-08-12 (Fase 44) — Lista progetti DINAMICA:
  //   1) Se `activeSlugs` fornito dal parent (es. da /api/public/properties),
  //      lo rispettiamo (source of truth: backend, filtra i progetti spenti).
  //   2) Altrimenti fallback: prende tutti i progetti "visibili" da RESORT_FINANCIALS.
  const RESORT_KEYS = useMemo(() => {
    if (Array.isArray(activeSlugs) && activeSlugs.length > 0) {
      return activeSlugs.filter((k) => !!RESORT_FINANCIALS[k]);
    }
    const keys = Object.keys(RESORT_FINANCIALS);
    return keys.filter((k) => {
      const r = RESORT_FINANCIALS[k];
      if (!r) return false;
      const hasEquity = !!r.equity?.available;
      const hasRE = !!r.realEstate?.available;
      const isEO = !!r.equityOnly;
      return hasEquity || hasRE || isEO;
    });
  }, [activeSlugs]);

  const data = useMemo(() => {
    const out = {};
    for (const k of RESORT_KEYS) {
      const row = buildRow(k);
      if (row) out[k] = row;
    }
    return out;
  }, [RESORT_KEYS]);

  const [activeMobile, setActiveMobile] = useState(RESORT_KEYS[0]);

  // Se non ci sono progetti, mostra placeholder
  if (RESORT_KEYS.length === 0) return null;

  // Calcolo grid template dinamico per la tabella desktop
  const gridTemplate = `200px repeat(${RESORT_KEYS.length}, minmax(200px, 1fr))`;

  const renderCell = (rKey, key) => {
    const v = data[rKey]?.[key];
    if (v === undefined || v === null) return '—';
    if (key === 'units' && data[rKey].unitsEquityOnly) {
      return (
        <span className="inline-flex items-center gap-1.5 text-[var(--gp-gold)] font-semibold">
          <Sparkles className="w-3.5 h-3.5" />
          {v}
        </span>
      );
    }
    if (key === 'units' && data[rKey].unitsSoldOut) {
      return (
        <span className="inline-flex items-center gap-1.5 text-red-600 font-semibold">
          <AlertTriangle className="w-3.5 h-3.5" />
          {v}
        </span>
      );
    }
    if (key === 'realEstate' && !data[rKey].realEstateAvailable) {
      return <span className="text-[var(--gp-forest)]/55">{v}</span>;
    }
    return v;
  };

  return (
    <section
      className="px-4 md:px-12 py-16 md:py-24 bg-[var(--surface-1)]"
      data-testid="resort-compare-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[var(--gp-gold)] font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            Confronto a colpo d&apos;occhio
          </div>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.05] mt-3 md:mt-4 text-[var(--gp-forest)]">
            Quale progetto fa per te?
          </h2>
          <p className="text-sm md:text-base text-[var(--gp-forest)]/70 mt-4 max-w-2xl mx-auto leading-relaxed">
            {RESORT_KEYS.length} progetti attivi, {new Set(RESORT_KEYS.map((k) => RESORT_FINANCIALS[k]?.country).filter(Boolean)).size} paesi, ticket d&apos;ingresso diversi.
            Confrontali fianco a fianco — poi scegli con il modulo guidato.
          </p>
        </div>

        {/* Desktop / Tablet: N-column comparison (scroll-x se serve) */}
        <div className="hidden md:block overflow-x-auto">
          <div
            className="rounded-2xl overflow-hidden border border-[var(--gp-forest)]/12 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] min-w-full"
            style={{ display: 'grid', gridTemplateColumns: gridTemplate }}
          >
            {/* Header row — resort names */}
            <div className="bg-[var(--surface-2,#f6f3ec)] py-5 px-4 border-b border-[var(--gp-forest)]/10" />
            {RESORT_KEYS.map((k) => (
              <div
                key={`h-${k}`}
                className="bg-[var(--gp-forest)] text-[var(--surface-1)] py-5 px-4 border-b border-[var(--gp-forest)]/30 text-center"
                data-testid={`compare-header-${k}`}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">
                  {data[k]?.country}
                </div>
                <div className="font-serif text-xl mt-1 leading-tight">
                  {data[k]?.shortName}
                </div>
              </div>
            ))}

            {/* Data rows */}
            {ROWS.map((row) => {
              const Icon = row.icon;
              return (
                <React.Fragment key={row.key}>
                  <div
                    className={`py-4 px-4 border-b border-[var(--gp-forest)]/8 ${row.highlight ? 'bg-[var(--gp-gold)]/8' : 'bg-[var(--surface-2,#f6f3ec)]'} flex items-center gap-2 text-[12px] md:text-[13px] uppercase tracking-[0.16em] text-[var(--gp-forest)]/75 font-semibold`}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5 text-[var(--gp-gold)]" />}
                    {row.label}
                  </div>
                  {RESORT_KEYS.map((k, ci) => (
                    <div
                      key={`${row.key}-${k}`}
                      className={`py-4 px-4 border-b border-[var(--gp-forest)]/8 ${row.highlight ? 'bg-[var(--gp-gold)]/4' : ''} ${ci < RESORT_KEYS.length - 1 ? 'border-r border-[var(--gp-forest)]/8' : ''} text-[14px] md:text-[15px] text-[var(--gp-forest)] ${row.highlight ? 'font-semibold' : ''}`}
                      data-testid={`compare-${k}-${row.key}`}
                    >
                      {renderCell(k, row.key)}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}

            {/* Footer CTA row */}
            <div className="py-5 px-4 bg-[var(--surface-2,#f6f3ec)]" />
            {RESORT_KEYS.map((k) => (
              <div
                key={`cta-${k}`}
                className="py-5 px-4 bg-[var(--surface-2,#f6f3ec)] border-r border-[var(--gp-forest)]/8 last:border-r-0 text-center"
              >
                <Button
                  onClick={() => openQualifica({ source: `compare-table-${k}` })}
                  className="w-full h-10 text-xs font-semibold tracking-[0.05em] uppercase"
                  data-testid={`compare-cta-${k}`}
                >
                  Scopri {data[k]?.shortName}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: tab switcher + single column */}
        <div className="md:hidden">
          <div className="flex gap-1.5 p-1 rounded-full bg-[var(--surface-2,#f6f3ec)] border border-[var(--gp-forest)]/10 mb-5 overflow-x-auto">
            {RESORT_KEYS.map((k) => (
              <button
                key={`tab-${k}`}
                type="button"
                onClick={() => setActiveMobile(k)}
                className={`shrink-0 flex-1 py-2.5 px-3 rounded-full text-[11px] font-semibold tracking-[0.1em] uppercase transition-colors whitespace-nowrap ${
                  activeMobile === k
                    ? 'bg-[var(--gp-forest)] text-[var(--surface-1)] shadow'
                    : 'text-[var(--gp-forest)]/60'
                }`}
                data-testid={`compare-tab-${k}`}
              >
                {data[k]?.shortName}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-[var(--gp-forest)]/12 overflow-hidden shadow-lg">
            <div className="bg-[var(--gp-forest)] text-[var(--surface-1)] py-5 px-5 text-center">
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-70">
                {data[activeMobile]?.country}
              </div>
              <div className="font-serif text-2xl mt-1">
                {data[activeMobile]?.shortName}
              </div>
            </div>
            <div>
              {ROWS.map((row) => {
                const Icon = row.icon;
                return (
                  <div
                    key={`m-${row.key}`}
                    className={`px-5 py-3.5 border-b border-[var(--gp-forest)]/8 ${row.highlight ? 'bg-[var(--gp-gold)]/8' : ''} flex items-start justify-between gap-3`}
                  >
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[var(--gp-forest)]/65 font-semibold flex-shrink-0 pt-1">
                      {Icon && <Icon className="w-3 h-3 text-[var(--gp-gold)]" />}
                      {row.label}
                    </span>
                    <span
                      className={`text-[13px] text-[var(--gp-forest)] text-right ${row.highlight ? 'font-semibold' : ''}`}
                      data-testid={`compare-mobile-${activeMobile}-${row.key}`}
                    >
                      {renderCell(activeMobile, row.key)}
                    </span>
                  </div>
                );
              })}
              <div className="p-4">
                <Button
                  onClick={() => openQualifica({ source: `compare-mobile-${activeMobile}` })}
                  className="w-full h-11 text-xs font-semibold tracking-[0.05em] uppercase"
                  data-testid={`compare-cta-mobile-${activeMobile}`}
                >
                  Scopri {data[activeMobile]?.shortName}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <p
          className="mt-8 md:mt-10 text-[11px] md:text-xs text-[var(--gp-forest)]/55 leading-relaxed max-w-3xl mx-auto text-center"
          data-testid="compare-consob-disclaimer"
        >
          I dati riportati nella tabella sono informazioni di progetto. Le proiezioni
          economiche delle singole SPV sono di natura indicativa, soggette alle
          variabili di mercato e non costituiscono offerta al pubblico, consulenza
          finanziaria o promessa di rendimento. La documentazione completa viene
          condivisa solo in fase di qualifica.
        </p>
      </div>
    </section>
  );
}
